import fetcher from "./fetcher";
import createTag from "./tags/create";
import { showMessage } from "./utils/alert";
import { stringifyError, TypeableSelectError } from "./utils/error";
import validateSubjects from "./validate/subjects";

const onCountryChange = async () => {
  const countryHiddenInput =
    document.querySelector("#country_id")?.previousSibling;
  const currency = document.querySelector<HTMLInputElement>("#user_currency");
  const cellNo = document.querySelector<HTMLInputElement>("#cell-no");

  if (!(countryHiddenInput instanceof HTMLInputElement)) return;

  const country_id = countryHiddenInput.value;

  if (!country_id) return;

  try {
    const res = await fetcher(`/users/country-data?country_id=${country_id}`);

    if (!res.ok) throw Error("Unexpected error while getting country data!");

    const countryData = await res.json();

    document
      .querySelectorAll<HTMLElement>(".country-code")
      .forEach((element) => {
        element.innerText = `+${countryData.country_code}`;
      });

    if (currency)
      currency.value = countryData.country_code === "92" ? "PKR" : "USD";

    if (cellNo) {
      cellNo.minLength = countryData.country_code === "92" ? 10 : 7;
      cellNo.maxLength = countryData.country_code === "92" ? 11 : 14;
    }
  } catch (e) {
    console.error(e);
    console.error("Unexpected error while getting country data!");
  }
};

const removeLanguage = (language: string) => {
  document
    .querySelectorAll(`[data-language='${language}']`)
    .forEach((element) => element.remove());
  validateLanguages();
};

// Add language input and tag to be displayed
const addLanguages = (
  inputVal = document.querySelector<HTMLSelectElement>("#language-input")
    ?.value,
) => {
  if (!inputVal) return;

  const languageInput =
    document.querySelector<HTMLSelectElement>("#language-input");
  const tagContainer = document.querySelector("#tag-container");
  const hiddenFieldsContainer = document.querySelector(
    "#hidden-field_language",
  );

  if (!languageInput?.dataset.options)
    throw new TypeableSelectError("Language options not found!");
  if (!tagContainer)
    throw new TypeableSelectError("Language Tag container not found!");
  if (!hiddenFieldsContainer)
    throw new TypeableSelectError(
      "Language Hidden fields container not found!",
    );

  try {
    if (!document.querySelectorAll(`[data-language='${inputVal}']`).length) {
      const options = JSON.parse(languageInput.dataset.options).map(
        (elem: any) => elem[0].toLowerCase(),
      );

      if (!options.includes(inputVal.trim().toLowerCase())) {
        throw new TypeableSelectError(
          "Please select an option from the dropdown",
        );
      }

      const tag = createTag("language", inputVal);
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "languages[]";
      input.dataset.language = inputVal;
      input.value = inputVal;

      tagContainer.appendChild(tag);
      hiddenFieldsContainer.appendChild(input);
      tag.addEventListener("click", () => removeLanguage(inputVal));
    } else {
      throw new TypeableSelectError(
        `You have already selected "${inputVal}". Please select some other language.`,
      );
    }
  } catch (e) {
    console.error(e);
    languageInput.focus();
    showMessage(stringifyError(e));
  } finally {
    languageInput.value = "";
    languageInput.dispatchEvent(new Event("filter_options"));
    validateLanguages();
  }
};

// validation on subject button tags
const validateLanguages = () => {
  const languageInput =
    document.querySelector<HTMLInputElement>("#language-input");
  if (!languageInput) return false;

  if (document.querySelectorAll("[data-language]").length === 0) {
    languageInput.classList.add("is-invalid");
    languageInput.classList.remove("is-valid");
    languageInput.setCustomValidity(
      "You must select at least one language...!",
    );
    return false;
  } else {
    languageInput.classList.add("is-valid");
    languageInput.classList.remove("is-invalid");
    languageInput.setCustomValidity("");
    return true;
  }
};

const handleCountryDisclaimer = () => {
  const countryHiddenEl =
    document.querySelector("#country_id")?.previousElementSibling;
  if (!(countryHiddenEl instanceof HTMLInputElement)) return;

  const selectedId = countryHiddenEl.value;
  const ipCountryId =
    document.querySelector<HTMLInputElement>("#country_id")?.dataset
      .ipCountryId;
  const disclaimerEl = document.getElementById("country-disclaimer");
  const checkboxEl = document.getElementById(
    "country-disclaimer-checkbox",
  ) as HTMLInputElement | null;

  if (!disclaimerEl || !checkboxEl || !ipCountryId) return;

  const mismatch = !!selectedId && selectedId !== ipCountryId;
  disclaimerEl.style.display = mismatch ? "block" : "none";
  checkboxEl.checked = false;

  if (!mismatch) {
    checkboxEl.setCustomValidity("");
  } else {
    checkboxEl.setCustomValidity(
      "Please acknowledge the country disclaimer before proceeding.",
    );
  }
};

const validateCountryDisclaimer = (): boolean => {
  const disclaimerEl = document.getElementById("country-disclaimer");
  const checkboxEl = document.getElementById(
    "country-disclaimer-checkbox",
  ) as HTMLInputElement | null;

  if (!disclaimerEl || disclaimerEl.style.display === "none") return true;
  if (!checkboxEl) return true;

  if (checkboxEl.checked) {
    checkboxEl.setCustomValidity("");
    checkboxEl.reportValidity();
    return true;
  }

  checkboxEl.setCustomValidity(
    "Please acknowledge the country disclaimer before proceeding.",
  );
  checkboxEl.reportValidity();
  return false;
};

const addCountryListeners = () => {
  onCountryChange();
  const country_id = document.querySelector("#country_id");
  const disclaimerCheckbox = document.getElementById(
    "country-disclaimer-checkbox",
  ) as HTMLInputElement | null;

  if (disclaimerCheckbox) {
    disclaimerCheckbox.addEventListener("change", validateCountryDisclaimer);
  }
  country_id?.addEventListener("selected", onCountryChange);
  country_id?.addEventListener("selected", handleCountryDisclaimer);
  document
    .querySelector("#country-code")
    ?.addEventListener("click", () =>
      country_id?.dispatchEvent(new Event("focus")),
    );
};

const addLanguageListeners = () => {
  const languageInput = document.getElementById("language-input");

  if (!languageInput) return;

  languageInput.addEventListener("focus", () =>
    languageInput.setAttribute("placeholder", "Start typing language..."),
  );
  languageInput.addEventListener("blur", () =>
    languageInput.setAttribute("placeholder", "Add Languages"),
  );
  languageInput.addEventListener("selected", () => addLanguages());
  languageInput.dataset.selected
    ?.split(";")
    ?.forEach((language) => addLanguages(language));
};

const countLettersListener = () => {
  const intro = document.querySelector<HTMLTextAreaElement>("#intro");
  const messageChar = document.querySelector<HTMLElement>("#message-char");
  const minChar = document.querySelector<HTMLElement>("#min-char");

  if (!intro || !messageChar || !minChar) return;

  intro.addEventListener("input", () => {
    const length = [...intro.value].length;

    messageChar.innerText = length.toString();
    minChar.innerText = (
      length < intro.minLength + 1 ? intro.minLength - length : 0
    ).toString();
  });
};

document.addEventListener("DOMContentLoaded", () => {
  const form = document.querySelector<HTMLFormElement>("#educational-form");
  form?.addEventListener(
    "submit",
    (e) => {
      console.count("submit");
      validateSubjects();
      validateLanguages();
      if (!form.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    false,
  );

  addCountryListeners();
  countLettersListener();
  validateSubjects();
  validateLanguages();

  // Add callback to recaptcha v3 on registration form submit
  const hiddenInput = document.querySelector<HTMLInputElement>(
    "#g-recaptcha-response-data-registration",
  );
  document
    .querySelectorAll<HTMLFormElement>(".registration-form")
    .forEach((registrationForm) => {
      registrationForm.addEventListener("submit", async function (e) {
        e.preventDefault();

        if (!validateCountryDisclaimer()) return;

        if (
          "executeRecaptchaForRegistrationAsync" in window &&
          window.executeRecaptchaForRegistrationAsync instanceof Function
        ) {
          const response = await window.executeRecaptchaForRegistrationAsync();
          if (hiddenInput) hiddenInput.value = response;
          registrationForm.submit();
        }
      });
    });

  document
    .querySelectorAll<HTMLFormElement>(".country-form")
    .forEach((countryForm) => {
      countryForm.addEventListener(
        "submit",
        (e) => {
          validateCountry();
          if (!validateCountryDisclaimer()) {
            e.preventDefault();
            e.stopPropagation();
          } else if (!countryForm.checkValidity()) {
            countryForm.reportValidity();
            e.preventDefault();
            e.stopPropagation();
          } else {
            const btn = countryForm.querySelector('button[type="submit"]');

            if (!btn) return;

            btn.innerHTML = `
          <i class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </i>
        `;
            btn.setAttribute("disabled", "true");
          }
        },
        false,
      );
    });

  document
    .querySelector("#country_id")
    ?.addEventListener("selected", validateCountry);

  addLanguageListeners();

  document
    .getElementById("tutor-edit-personal-form")
    ?.addEventListener("submit", (e) => {
      if (!validateCountryDisclaimer()) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
});

const validateCountry = () => {
  const typeableSelect =
    document.querySelector<HTMLSelectElement>("#country_id");

  if (!typeableSelect) return false;

  const countryInput = typeableSelect.previousElementSibling;

  if (!(countryInput instanceof HTMLInputElement)) return false;

  if (countryInput.value) {
    typeableSelect.classList.add("is-valid");
    typeableSelect.classList.remove("is-invalid");
    typeableSelect.setCustomValidity("");
    return true;
  } else {
    typeableSelect.classList.add("is-invalid");
    typeableSelect.classList.remove("is-valid");
    typeableSelect.setCustomValidity("You must select a country!");
    return false;
  }
};

document.addEventListener("listen-to-country", addCountryListeners);
document.addEventListener("count-letters", countLettersListener);
